/**
 * Uma entrada por item do menu lateral (`frontend/src/components/layout.tsx`).
 * A ordem é a do menu, para o modal listar na mesma sequência que a pessoa vê
 * na tela.
 */
export const SECOES = [
    {
        codigo: 'monitor',
        rotulo: 'Monitor de OS',
        descricao: 'O quadro de ordens por técnico, e a ficha de cada OS.',
        caminho: '/',
    },
    {
        codigo: 'os_nova',
        rotulo: 'Gerar OS',
        descricao: 'Abrir ordem de serviço nova no balcão.',
        caminho: '/os/nova',
    },
    {
        codigo: 'orcamento',
        rotulo: 'Orçamento',
        descricao: 'Montar e conferir o orçamento de uma OS.',
        caminho: '/orcamento',
    },
    {
        codigo: 'clientes',
        rotulo: 'Base de Clientes',
        descricao: 'Lista de clientes e o histórico de cada um.',
        caminho: '/clientes',
    },
    {
        codigo: 'caixa',
        rotulo: 'Caixa',
        descricao: 'Livro-caixa: entradas, saídas e saldo.',
        caminho: '/caixa',
    },
    {
        codigo: 'caixa_lancamento',
        rotulo: 'Lançamento',
        descricao: 'Lançar entrada ou saída no caixa.',
        caminho: '/caixa/novo',
    },
    {
        codigo: 'mensagens',
        rotulo: 'Mensagens',
        descricao: 'Disparo de WhatsApp para o cliente.',
        caminho: '/mensagens',
    },
    {
        codigo: 'dashboard',
        rotulo: 'Dashboard',
        descricao: 'Faturamento, ticket médio e lucro.',
        caminho: '/relatorios/dashboard',
    },
    {
        codigo: 'os_zeradas',
        rotulo: 'OS Zeradas',
        descricao: 'Ordens sem valor lançado.',
        caminho: '/relatorios/os-zeradas',
    },
    {
        codigo: 'admin',
        rotulo: 'Admin',
        descricao: 'Contas de acesso, permissões e fluxo de automações.',
        caminho: '/admin',
    },
];
export const CODIGOS_SECAO = SECOES.map((s) => s.codigo);
const TODAS = SECOES.map((s) => s.codigo);
/**
 * O que cada papel enxerga quando ninguém escolheu nada para a conta.
 *
 * É o comportamento que o sistema já tinha na prática — `admin` via a tela de
 * Admin e os demais viam o menu inteiro. A diferença é que agora `tecnico` e
 * `leitura` chegam com um menu enxuto, que é o que se pedia manualmente.
 */
export const SECOES_PADRAO = {
    admin: TODAS,
    operador: [
        'monitor',
        'os_nova',
        'orcamento',
        'clientes',
        'caixa',
        'caixa_lancamento',
        'mensagens',
        'dashboard',
        'os_zeradas',
    ],
    tecnico: ['monitor', 'os_nova', 'orcamento', 'clientes'],
    leitura: ['monitor', 'clientes', 'dashboard', 'os_zeradas'],
};
export function ehCodigoSecao(valor) {
    return TODAS.includes(valor);
}
/**
 * A lista CRUA da conta, limpa e na ordem do menu — preservando `null`.
 *
 * A ordem em que os códigos foram gravados não é a ordem em que a pessoa os
 * marcou (o banco agrupa e desduplica), e mostrar "Caixa, Monitor" para quem vê
 * "Monitor, Caixa" na barra lateral confunde à toa. Código desconhecido cai
 * fora: uma seção removida do sistema não pode sobreviver como caixinha órfã.
 */
export function normalizarSecoes(secoes) {
    if (!secoes)
        return null;
    const conjunto = new Set(secoes.filter(ehCodigoSecao));
    return TODAS.filter((codigo) => conjunto.has(codigo));
}
/**
 * A lista final que o menu usa.
 *
 * Duas travas que não são cosméticas:
 *  • código desconhecido é descartado — uma seção removida do sistema não pode
 *    sobreviver como item de menu que aponta para lugar nenhum;
 *  • papel `admin` mantém a seção `admin` de qualquer jeito. Sem isso, um
 *    administrador que desmarcasse a própria caixinha perderia a tela onde se
 *    desfaz o engano, e a volta seria pelo psql.
 */
export function secoesEfetivas(papel, secoes) {
    const escolhidas = secoes?.length ? secoes.filter(ehCodigoSecao) : SECOES_PADRAO[papel];
    const conjunto = new Set(escolhidas);
    if (papel === 'admin')
        conjunto.add('admin');
    // Devolve na ordem do menu, e não na ordem em que foram gravadas.
    return TODAS.filter((codigo) => conjunto.has(codigo));
}
/**
 * A qual seção pertence um caminho do frontend.
 *
 * Existe porque nem toda rota está no menu: a ficha da OS, a via em PDF e a
 * troca de status são alcançadas pelo quadro, então pertencem ao Monitor; o
 * histórico do cliente pertence à Base de Clientes. Sem este mapa, esconder o
 * item do menu deixaria a URL aberta — que é o mesmo que não esconder.
 *
 * `null` = rota sem dono (login, redirecionamento legado, 404): não é seção, e
 * portanto não é bloqueada aqui.
 */
export function secaoDaRota(caminho) {
    const limpo = (caminho.split('?')[0] ?? '').replace(/\/+$/, '') || '/';
    const regras = [
        [/^\/$/, 'monitor'],
        [/^\/os\/nova$/, 'os_nova'],
        // Antes da regra genérica de `/os/...`: a tela de orçamento mora sob a OS.
        [/^\/os\/[^/]+\/orcamento$/, 'orcamento'],
        [/^\/orcamento$/, 'orcamento'],
        [/^\/os\//, 'monitor'],
        [/^\/clientes(\/|$)/, 'clientes'],
        [/^\/caixa\/novo$/, 'caixa_lancamento'],
        [/^\/caixa$/, 'caixa'],
        [/^\/mensagens$/, 'mensagens'],
        [/^\/relatorios\/dashboard$/, 'dashboard'],
        [/^\/relatorios\/os-zeradas$/, 'os_zeradas'],
        [/^\/admin(\/|$)/, 'admin'],
    ];
    return regras.find(([padrao]) => padrao.test(limpo))?.[1] ?? null;
}
//# sourceMappingURL=secoes.js.map